import { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDesignPatternStudio } from "../DesignPatternStudioContext";
import { ACTIONS } from "../reducers/studioReducer";
import ProfileWidthAccordion from "./ProfileWidthAccordion";

const useStyles = makeStyles(() => ({
    root: {
      width: '100%',
    },
}));

export const getConfigSwitchLabel = (state) => {
    if (state.insideFeature) {
      return 'inside_config';
    } else {
      return 'base_config';
    }
}

const ApprovalMapping = () => {
    const classes = useStyles();
    const { state, dispatch } = useDesignPatternStudio();
    const [expanded, setExpanded] = useState(null);

    const configSwitchLabel = getConfigSwitchLabel(state);

    const handleAccordionChange = (profileData) => (event, isExpanded) => {
        const mappingProfile = state.mappingData.profiles.find((profile) => profile.sku_code === profileData.sku_code);
        setExpanded(isExpanded ? mappingProfile.id : null);
    };

    const handleWidthToggle = (profileId, widthId, checked) => {
        const updatedData = {
            ...state.mappingData,
            profiles: state.mappingData.profiles.map((profile) => {
                if (profile.id === profileId) {
                    return {
                        ...profile,
                        children: profile.children.map((width) => {
                            if(width.id === widthId){
                                return {
                                    ...width,
                                    checked,
                                }
                            }
                            return width;
                        }),
                    };
                }
                return profile;
            }),
        };

        dispatch({
            type: ACTIONS.SET_MAPPING_DATA,
            payload: updatedData,
        });
    };

    const handleProfileToggle = (profileId, checked) => {
            const updatedData = {
                ...state.mappingData,
                profiles: state.mappingData.profiles.map((profile) => {
                    if (profile.id === profileId) {
                        return {
                            ...profile,
                            checked,
                        };
                    }
                    return profile;
            }),
        };

        dispatch({
            type: ACTIONS.SET_MAPPING_DATA,
            payload: updatedData,
        });
    };

    const handleScaleChange = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            scale: value,
                        }
                    }
                }
        };
        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handlePaddingChange = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            padding: value,
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleShadowChange = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            depth: value,
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleRotationChange = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            shape_rotation: value,
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleWarpChange = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            warp: value,
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleDisplacementChange = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            displacement: value,
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleManualWarpToggle = (profileId, widthId, value) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            // Here when you try to manual warp, you need to reset the padding, warp, and displacement to 0. So that user can edit from scratch to get better results.
                            scale: value ? 0 : profileConfig[widthId].config[configSwitchLabel].scale,
                            padding: value ? 0 : profileConfig[widthId].config[configSwitchLabel].padding,
                            warp: value ? 0 : profileConfig[widthId].config[configSwitchLabel].warp,
                            displacement: value ? 50 : profileConfig[widthId].config[configSwitchLabel].displacement,
                            manualWarp: {
                              isEnabled: value,
                              meshComplexity: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.meshComplexity ?? 3,
                              interpolation: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.interpolation ?? 50,
                              controlPoints: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.controlPoints ?? [],
                              manualWarpSvg: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.manualWarpSvg ?? "",
                              viewBoxScale: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.viewBoxScale ?? 1,
                              patternPosition: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.patternPosition ?? { x: 0, y: 0 },
                              patternScale: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.patternScale ?? 1,
                              // warpOffset: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.warpOffset ?? { warpTranslateX: 0, warpTranslateY: 0, warpScale: 1 },
                              history: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.history ?? [],
                              historyIndex: profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.historyIndex ?? -1,
                            }
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleManualWarpUpdate = (profileId, widthId, warpData) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            manualWarp: {
                              ...profileConfig[widthId].config[configSwitchLabel]?.manualWarp,
                              meshComplexity: warpData?.meshComplexity || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.meshComplexity,
                              interpolation: warpData?.interpolation || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.interpolation,
                              controlPoints: warpData?.controlPoints || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.controlPoints,
                              manualWarpSvg: warpData?.manualWarpSvg || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.manualWarpSvg,
                              viewBoxScale: warpData?.viewBoxScale || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.viewBoxScale,
                              patternPosition: warpData?.patternPosition || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.patternPosition,
                              patternScale: warpData?.patternScale !== undefined ? warpData.patternScale : (profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.patternScale || 1),
                              // warpOffset: warpData?.warpOffset || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.warpOffset,
                              history: warpData?.history || profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.history,
                              historyIndex: warpData?.historyIndex ?? profileConfig[widthId].config[configSwitchLabel]?.manualWarp?.historyIndex,
                            }
                        }
                    }
                }
        };

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    }

    const handlePositionChange = (profileId, widthId, xValue, yValue) => {
        const profileConfig = state.profileConfigs[profileId];
        const updatedData = {
            ...profileConfig,
            [widthId]: {
                    ...profileConfig[widthId],
                    config: {
                        ...profileConfig[widthId].config,
                        [configSwitchLabel]: {
                            ...profileConfig[widthId].config[configSwitchLabel],
                            shape_x: xValue,
                            shape_y: yValue,
                        }
                    }
                }
        };
        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId, configs: updatedData } });
    };

    const handleProfileScaleChange = (profileSkuCode, difference, event) => {
        const profileConfig = state.profileConfigs[profileSkuCode];
        const updatedData = Object.keys(profileConfig).reduce((acc, width) => {
            const profileWidth = state.mappingData.profiles.find((profile) => profile.sku_code === profileSkuCode)?.children.find((w) => w.sku_code.toString() === width);
            if(profileWidth?.checked) {
                acc[width] = {
                    ...profileConfig[width],
                    config: {
                        ...profileConfig[width].config,
                        [configSwitchLabel]: {
                            ...profileConfig[width].config[configSwitchLabel],
                            scale: Math.max(-50, Math.min(50, (profileConfig[width]?.config?.[configSwitchLabel]?.scale || 0) + difference))
                        }
                    }
                };
            }
            return acc;
        }, {});

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId: profileSkuCode, configs: updatedData } });
    };

    const handleProfilePaddingChange = (profileSkuCode, difference, event) => {
        const profileConfig = state.profileConfigs[profileSkuCode];
        const updatedData = Object.keys(profileConfig).reduce((acc, width) => {
            const profileWidth = state.mappingData.profiles.find((profile) => profile.sku_code === profileSkuCode)?.children.find((w) => w.sku_code.toString() === width);
            if(profileWidth?.checked) {
                acc[width] = {
                    ...profileConfig[width],
                    config: {
                        ...profileConfig[width].config,
                        [configSwitchLabel]: {
                            ...profileConfig[width].config[configSwitchLabel],
                            padding: Math.max(0, Math.min(50, (profileConfig[width].config[configSwitchLabel].padding || 0) + difference))
                        }
                    }
                };
            }
            return acc;
        }, {});

        dispatch({ type: ACTIONS.UPDATE_PROFILE_CONFIG, payload: { profileId: profileSkuCode, configs: updatedData } });
    };

    return (
        <div className={classes.root}>
            {state.mappingData?.profiles?.map((profile) => {
                return (
                    <ProfileWidthAccordion
                        key={profile.id}
                        profile={profile}
                        expanded={expanded}
                        profileConfig={state.profileConfigs[profile.sku_code]}
                        onAccordionChange={handleAccordionChange}
                        onWidthToggle={handleWidthToggle}
                        onProfileToggle={handleProfileToggle}
                        onWidthScaleChange={handleScaleChange}
                        onWidthPaddingChange={handlePaddingChange}
                        onShadowChange={handleShadowChange}
                        onRotationChange={handleRotationChange}
                        onWarpChange={handleWarpChange}
                        onDisplacementChange={handleDisplacementChange}
                        onPositionChange={handlePositionChange}
                        onManualWarpToggle={handleManualWarpToggle}
                        onManualWarpUpdate={handleManualWarpUpdate}
                        onProfileScaleChange={handleProfileScaleChange}
                        onProfilePaddingChange={handleProfilePaddingChange}
                        configSwitchLabel={configSwitchLabel}
                    />
                );
            })}
        </div>
    )
}
export default ApprovalMapping;